import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Menu, Sun, Moon } from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";

const Nav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuActive, setIsMenuActive] = useState(false);
  const { login, logout, isLoggedIn } = useAuth();
  const { isDarkTheme, toggleTheme } = useTheme();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const navigate = useNavigate();
  const [lastScrollY, setLastScrollY] = useState(0);
  const [visible, setVisible] = useState(true);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY && currentScrollY > 50) {
      // Scrolling down
      setVisible(false);
    } else {
      // Scrolling up
      setVisible(true);
    }

    setLastScrollY(currentScrollY);
    setIsScrolled(currentScrollY > 50);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMenuActive(false);
  };

  const toggleMenu = () => {
    setIsMenuActive(!isMenuActive);
  };

  const handleAuthAction = () => {
    if (isLoggedIn) {
      navigate("/chat");
    } else {
      navigate("/auth");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setIsMenuActive(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const sections = [
    { id: "services", label: "Services" },
    { id: "workflow", label: "Workflow" },
    { id: "benefits", label: "Benefits" },
    { id: "pricing-cta", label: "Pricing" },
    // { id: "testimonials", label: "Testimonials" },
    { id: "faqs", label: "FAQs" },
  ];

  return (
    <nav
      className={`flex justify-between items-center p-5 fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-3 w-full ${
        isDarkTheme
          ? "bg-dark-background text-white"
          : "bg-amber-50 text-black border-b border-gray-200"
      } ${visible ? "translate-y-0" : "-translate-y-full"}`}
    >
      <div className="container mx-auto flex justify-between items-center max-w-7xl gap-10">
        <div className="text-xl font-semibold tracking-wide font-sans">
          <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
            OG
          </span>
          <span className={isDarkTheme ? "text-white" : "text-gray-800"}>
            -FLEET
          </span>
        </div>

        {/* Mobile: Hamburger Menu + Theme Toggle */}
        <div className="lg:hidden flex items-center gap-4">
          {/* Theme Toggle */}
          {isDarkTheme ? (
            <Sun
              className="w-5 h-5 text-amber-400 cursor-pointer transition-transform duration-300"
              onClick={toggleTheme}
            />
          ) : (
            <Moon
              className="w-5 h-5 text-gray-600 cursor-pointer transition-transform duration-300"
              onClick={toggleTheme}
            />
          )}
          {/* Mobile Menu Icon */}
          <div
            className="cursor-pointer"
            onClick={toggleMenu}
            ref={hamburgerRef}
          >
            <Menu
              className={`w-6 h-6 ${isDarkTheme ? "text-white" : "text-black"}`}
            />
          </div>
        </div>

        {/* Desktop Navigation Links and Dynamic Auth Button */}
        <div className="hidden lg:flex items-center gap-8 font-medium text-md font-sans">
          <ul className="flex gap-5">
            {sections.map((section) => (
              <li
                key={section.id}
                className={`cursor-pointer transition-all duration-300 ${
                  isDarkTheme ? "hover:text-amber-400 text-white" : "hover:text-amber-600 text-black"
                }`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.label}
              </li>
            ))}
          </ul>

          {/* Theme Toggle - Always Visible */}
          <div className="relative group mr-4 lg:mr-0">
            {isDarkTheme ? (
              <Sun
                className="w-5 h-5 text-amber-400 cursor-pointer transition-transform duration-300 z-10"
                onClick={toggleTheme}
              />
            ) : (
              <Moon
                className="w-5 h-5 text-gray-600 cursor-pointer transition-transform duration-300 z-10"
                onClick={toggleTheme}
              />
            )}
            <div
              className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block ${
                isDarkTheme ? "text-white bg-gray-800" : "text-black bg-white"
              } p-2 rounded text-xs shadow-md`}
            >
              Toggle Theme
            </div>
          </div>

          {/* Dynamic Auth Button (Desktop) */}
          <div className="relative">
            <button
              onClick={handleAuthAction}
              className={`text-white rounded-lg px-6 py-2 text-md font-medium transition-all duration-300 ${
                isLoggedIn
                  ? "bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700"
                  : "bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800"
              }`}
            >
              {isLoggedIn ? "Chat" : "Sign Up"}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuActive && (
            <motion.div
              className={`lg:hidden absolute top-[60px] right-0 w-2/3 p-5 rounded-lg z-40 ${
                isDarkTheme
                  ? "bg-dark-background text-white border border-dark-border"
                  : "bg-white text-black border border-gray-200"
              }`}
              ref={menuRef}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
            >
              <ul>
                {sections.map((section) => (
                  <li
                    key={section.id}
                    className={`cursor-pointer p-3 font-sans font-medium text-lg ${
                      isDarkTheme ? "hover:text-amber-400" : "hover:text-amber-600"
                    }`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.label}
                  </li>
                ))}
              </ul>

              {/* Mobile Dynamic Auth Button */}
              <button
                onClick={handleAuthAction}
                className={`w-full rounded-full px-4 py-1 text-xl font-medium transition-all duration-300 ${
                  isLoggedIn
                    ? "bg-gradient-to-r from-amber-400 to-amber-600 text-white hover:from-amber-500 hover:to-amber-700"
                    : "bg-gradient-to-r from-amber-500 to-amber-700 text-white hover:from-amber-600 hover:to-amber-800"
                } mt-4`}
              >
                {isLoggedIn ? "Chat" : "Sign Up"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Nav;
